if (!customElements.get("product-property")) {
  const CROP_RATIOS = { circle: 1, square: 1, portrait: 4 / 5, landscape: 4 / 3 };
  const MAX_ZOOM = 4;
  const MAX_OUTPUT_SIZE = 1600;

  customElements.define(
    "product-property",
    class ProductProperty extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="file"]');
        this.preview = this.querySelector(".product-property__preview");
        if (!this.input || !this.preview || this.previewReady) return;
        this.previewReady = true;
        this.previewImg = this.preview.querySelector(".product-property__preview-img");
        this.editButton = this.preview.querySelector('[data-action="edit"]');
        this.removeButton = this.preview.querySelector('[data-action="remove"]');
        this.template = this.querySelector(".product-property__cropper-template");
        this.editorEnabled = this.dataset.imageEditor === "true" && !!this.template;
        this.cropRatio = CROP_RATIOS[this.dataset.cropShape] || 1;

        this.input.addEventListener("change", this.onFileSelected.bind(this));
        if (this.editButton) {
          this.editButton.hidden = !this.editorEnabled;
          this.editButton.addEventListener("click", () => this.openEditor().catch(() => {}));
        }
        if (this.removeButton) {
          this.removeButton.addEventListener("click", () => {
            this.clear();
            this.input.focus();
          });
        }
      }

      // Read by the add to cart code (product.js) so the cropped image is sent even if
      // the browser refuses to put it back into the file input.
      get croppedFile() {
        return this.resultFile || null;
      }

      disconnectedCallback() {
        this.closeEditor();
        this.revoke("originalUrl");
        this.revoke("resultUrl");
      }

      async onFileSelected() {
        const file = this.input.files && this.input.files[0];
        if (!file) {
          // Browsers clear the input when the file dialog is cancelled: keep the current image.
          if (this.resultFile) this.setInputFile(this.resultFile);
          return;
        }
        if (!file.type.startsWith("image/")) {
          this.clear();
          return;
        }
        this.pendingFile = file;
        this.pendingUrl = URL.createObjectURL(file);
        if (!this.editorEnabled) {
          this.commitPending();
          this.setResult(file);
          return;
        }
        try {
          await this.openEditor(this.pendingUrl);
        } catch (error) {
          // The browser cannot decode this image (e.g. HEIC): keep the original file.
          console.error("[product-property] Image editor could not be opened.", error);
          this.commitPending();
          this.setResult(file);
        }
      }

      commitPending() {
        if (!this.pendingFile) return;
        this.revoke("originalUrl");
        this.originalFile = this.pendingFile;
        this.originalUrl = this.pendingUrl;
        this.pendingFile = null;
        this.pendingUrl = null;
      }

      discardPending() {
        if (!this.pendingFile) return;
        URL.revokeObjectURL(this.pendingUrl);
        this.pendingFile = null;
        this.pendingUrl = null;
      }

      openEditor(url = this.originalUrl) {
        if (!url || this.modal) return Promise.resolve();
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            try {
              this.buildEditor(image);
              resolve();
            } catch (error) {
              reject(error);
            }
          };
          image.onerror = () => reject(new Error("Image could not be decoded"));
          image.src = url;
        });
      }

      buildEditor(image) {
        const fragment = this.template.content.cloneNode(true);
        this.modal = fragment.querySelector(".product-property-cropper");
        this.stage = this.modal.querySelector(".product-property-cropper__stage");
        this.frame = this.modal.querySelector(".product-property-cropper__frame");
        this.stageImage = this.modal.querySelector(".product-property-cropper__image");
        this.zoomInput = this.modal.querySelector(".product-property-cropper__zoom");
        if (!this.stage || !this.frame || !this.stageImage) {
          // Template from an older version of snippets/product-custom-field.liquid.
          this.modal = null;
          throw new Error("Crop popup markup is outdated: update snippets/product-custom-field.liquid");
        }
        this.sourceImage = image;
        this.state = { w: image.naturalWidth, h: image.naturalHeight, rotate: 0, zoom: 0, x: 0, y: 0 };

        this.stageImage.src = image.src;
        this.stageImage.style.setProperty("width", `${image.naturalWidth}px`, "important");
        this.stageImage.style.setProperty("height", `${image.naturalHeight}px`, "important");
        this.stageImage.style.setProperty("max-width", "none", "important");

        document.body.appendChild(fragment);
        document.body.classList.add("product-property-cropper-open");
        this.lastFocus = document.activeElement;

        this.onModalClick = this.handleModalClick.bind(this);
        this.onKeydown = (event) => {
          if (event.key === "Escape") this.cancelEditor();
        };
        this.onResize = () => this.layout();
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onWheel = this.handleWheel.bind(this);
        this.onZoomInput = () => {
          this.state.zoom = Number(this.zoomInput.value) / 100;
          this.render();
        };

        this.modal.addEventListener("click", this.onModalClick);
        document.addEventListener("keydown", this.onKeydown);
        window.addEventListener("resize", this.onResize);
        this.stage.addEventListener("pointerdown", this.onPointerDown);
        this.stage.addEventListener("pointermove", this.onPointerMove);
        this.stage.addEventListener("pointerup", this.onPointerUp);
        this.stage.addEventListener("pointercancel", this.onPointerUp);
        this.stage.addEventListener("wheel", this.onWheel, { passive: false });
        if (this.zoomInput) this.zoomInput.addEventListener("input", this.onZoomInput);

        this.layout();
        const cropButton = this.modal.querySelector('[data-action="crop"]');
        if (cropButton) cropButton.focus();
      }

      // Size the crop frame to fit the stage.
      layout() {
        if (!this.modal) return;
        const padding = 24;
        const maxWidth = Math.max(this.stage.clientWidth - padding * 2, 50);
        const maxHeight = Math.max(this.stage.clientHeight - padding * 2, 50);
        let width = maxWidth;
        let height = width / this.cropRatio;
        if (height > maxHeight) {
          height = maxHeight;
          width = height * this.cropRatio;
        }
        this.cropWidth = width;
        this.cropHeight = height;
        this.frame.style.width = `${width}px`;
        this.frame.style.height = `${height}px`;
        this.render();
      }

      rotatedSize() {
        const { w, h, rotate } = this.state;
        return rotate % 180 ? [h, w] : [w, h];
      }

      // The image always covers the crop frame; zoom goes from "cover" up to MAX_ZOOM times that.
      currentScale() {
        const [width, height] = this.rotatedSize();
        const coverScale = Math.max(this.cropWidth / width, this.cropHeight / height);
        return coverScale * (1 + this.state.zoom * (MAX_ZOOM - 1));
      }

      render() {
        const scale = this.currentScale();
        const [width, height] = this.rotatedSize();
        const maxX = Math.max(0, (width * scale - this.cropWidth) / 2);
        const maxY = Math.max(0, (height * scale - this.cropHeight) / 2);
        this.state.x = Math.min(maxX, Math.max(-maxX, this.state.x));
        this.state.y = Math.min(maxY, Math.max(-maxY, this.state.y));
        this.stageImage.style.transform =
          `translate(-50%, -50%) translate(${this.state.x}px, ${this.state.y}px) ` +
          `rotate(${this.state.rotate}deg) scale(${scale})`;
      }

      handlePointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;
        this.drag = { id: event.pointerId, startX: event.clientX, startY: event.clientY, x: this.state.x, y: this.state.y };
        this.stage.setPointerCapture(event.pointerId);
        this.stage.classList.add("is-dragging");
      }

      handlePointerMove(event) {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.state.x = this.drag.x + event.clientX - this.drag.startX;
        this.state.y = this.drag.y + event.clientY - this.drag.startY;
        this.render();
      }

      handlePointerUp(event) {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        this.drag = null;
        this.stage.classList.remove("is-dragging");
      }

      handleWheel(event) {
        event.preventDefault();
        this.setZoom(this.state.zoom - event.deltaY * 0.001);
      }

      setZoom(zoom) {
        this.state.zoom = Math.min(1, Math.max(0, zoom));
        if (this.zoomInput) this.zoomInput.value = Math.round(this.state.zoom * 100);
        this.render();
      }

      rotate(degree) {
        const { x, y } = this.state;
        this.state.rotate = (this.state.rotate + degree + 360) % 360;
        // Keep the same part of the image under the frame.
        this.state.x = degree > 0 ? -y : y;
        this.state.y = degree > 0 ? x : -x;
        this.render();
      }

      handleModalClick(event) {
        const button = event.target.closest("[data-action]");
        if (!button) return;
        switch (button.dataset.action) {
          case "rotate-left":
            this.rotate(-90);
            break;
          case "rotate-right":
            this.rotate(90);
            break;
          case "cancel":
            this.cancelEditor();
            break;
          case "crop":
            this.cropImage(button);
            break;
        }
      }

      cropImage(button) {
        const source = this.pendingFile || this.originalFile;
        const isCircle = this.dataset.cropShape === "circle";
        // A circle needs transparency outside the circle, so it is always saved as PNG.
        const type = isCircle || (source && source.type === "image/png") ? "image/png" : "image/jpeg";
        const scale = this.currentScale();
        const naturalWidth = this.cropWidth / scale;
        const naturalHeight = this.cropHeight / scale;
        const outputScale = Math.min(1, MAX_OUTPUT_SIZE / Math.max(naturalWidth, naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(naturalWidth * outputScale));
        canvas.height = Math.max(1, Math.round(naturalHeight * outputScale));

        const context = canvas.getContext("2d");
        if (type === "image/jpeg") {
          context.fillStyle = "#fff";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
        context.imageSmoothingQuality = "high";
        // Replay the on-screen transform, mapped from frame pixels to canvas pixels.
        context.translate(canvas.width / 2, canvas.height / 2);
        context.scale(canvas.width / this.cropWidth, canvas.height / this.cropHeight);
        context.translate(this.state.x, this.state.y);
        context.rotate((this.state.rotate * Math.PI) / 180);
        context.scale(scale, scale);
        context.drawImage(this.sourceImage, -this.state.w / 2, -this.state.h / 2);

        if (isCircle) {
          // Keep only the part inside the circle, like the frame shown in the popup.
          context.setTransform(1, 0, 0, 1, 0, 0);
          context.globalCompositeOperation = "destination-in";
          context.beginPath();
          context.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2, 0, Math.PI * 2);
          context.fill();
          context.globalCompositeOperation = "source-over";
        }

        button.disabled = true;
        canvas.toBlob(
          (blob) => {
            button.disabled = false;
            if (!blob) return;
            const baseName = ((source && source.name) || "image").replace(/\.[^.]+$/, "");
            const extension = type === "image/png" ? "png" : "jpg";
            const file = new File([blob], `${baseName}-cropped.${extension}`, { type, lastModified: Date.now() });
            this.commitPending();
            this.setResult(file);
            this.closeEditor();
          },
          type,
          0.92
        );
      }

      cancelEditor() {
        this.discardPending();
        if (this.resultFile) {
          this.setInputFile(this.resultFile);
        } else {
          this.input.value = "";
        }
        this.closeEditor();
      }

      closeEditor() {
        if (!this.modal) return;
        this.modal.removeEventListener("click", this.onModalClick);
        document.removeEventListener("keydown", this.onKeydown);
        window.removeEventListener("resize", this.onResize);
        this.modal.remove();
        this.modal = null;
        this.drag = null;
        this.sourceImage = null;
        document.body.classList.remove("product-property-cropper-open");
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }

      setResult(file) {
        this.resultFile = file;
        this.setInputFile(file);
        this.revoke("resultUrl");
        this.resultUrl = URL.createObjectURL(file);
        this.previewImg.src = this.resultUrl;
        this.previewImg.alt = file.name;
        this.preview.hidden = false;
      }

      setInputFile(file) {
        try {
          const transfer = new DataTransfer();
          transfer.items.add(file);
          this.input.files = transfer.files;
        } catch (e) {
          // Old browsers cannot set files programmatically: the originally selected file is sent instead.
        }
      }

      clear() {
        this.input.value = "";
        this.resultFile = null;
        this.originalFile = null;
        this.discardPending();
        this.revoke("originalUrl");
        this.revoke("resultUrl");
        this.preview.hidden = true;
        this.previewImg.removeAttribute("src");
        this.previewImg.alt = "";
      }

      revoke(key) {
        if (this[key]) {
          URL.revokeObjectURL(this[key]);
          this[key] = null;
        }
      }
    }
  );
}
